import React, { FunctionComponent, ReactNode, useEffect, useState } from 'react';
import {
    Col,
    Dropdown,
    DropdownButton,
    Row,
} from 'react-bootstrap';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import {
    AuthedLayout,
    EditableTableDiv,
    HeaderDisplayKeysWithType,
} from '../../../components';
import { useClassGrade, useSemGrade } from '../../../hook';
import { Sight, StudentGradeNoSeat, UserInfo, YearSem } from '../../../model';
import apis from '../../../apis';
import { SightContext } from '../../TableHeaders/SightHeader';
import {
    downloadDataAsExcel,
    printDataAsTable,
    SheetHeaderDisplay,
} from '../../../utils';
import { sightDiag } from '../../../utils/sight';
import { Nullable } from '../../../types';
import { DateTime } from 'luxon';
import { I18N } from '../../../i18n-raw';
import { toast } from 'react-toastify';

type PageData = StudentGradeNoSeat & Nullable<Sight> & { diag: string };
const manageIDMap: Record<string, string> = {
    '1': '長效散瞳劑\n(阿托平 Atropine)',
    '2': '短效散瞳劑',
    '3': '其他藥物',
    '4': '配鏡矯治',
    '5': '更換鏡片',
    '6': '遮眼治療',
    '7': '配戴隱形眼鏡',
    '8': '角膜塑型片',
    '9': '視力保健衛教',
    '10': '其他',
    '11': '定期檢查',
};
const formatSightValue = (value: unknown) => {
    if (typeof value !== 'number') return value as string;

    if (value === -1) return '<0.1';
    if (value < -1) return String(value);

    return Number.isInteger(value) ? value.toFixed(1) : String(value);
};
const tableHeader: (SheetHeaderDisplay<PageData> &
    HeaderDisplayKeysWithType<PageData>)[] = [
        { display: '年級', property: 'grade', style: { width: "4%" } },
        {
            display: '班級',
            property: 'className',
            onRender: (value: unknown) => {
                const num = parseInt(value as string, 10);
                return !isNaN(num) && num > 100 && num < 200 ? num - 100 : value;
            },
            onSheetRender: (value) => {
                const num = parseInt(value as string, 10);
                if (!isNaN(num)) {
                    return num > 100 && num < 200 ? num - 100 : num;
                }
                return value?.toString() ?? '';
            },
        },
        { display: '座號', property: 'seat', style: { width: "4%" } },
        { display: '姓名', property: 'name' },
        {
            display: '散瞳治療',
            property: 'isDilated',
            onRender: boolRender,
            onSheetRender: boolRender,
            style: { width: "3%" }
        },
        {
            display: '裸視右',
            property: 'sight0R',
            onRender: (value) => formatSightValue(value),
            onSheetRender: (value) => formatSightValue(value),
        },
        {
            display: '裸視左',
            property: 'sight0L',
            onRender: (value) => formatSightValue(value),
            onSheetRender: (value) => formatSightValue(value),
        },
        {
            display: '戴鏡右', property: 'sightR', style: { width: "4%" },
            onRender: (value) => formatSightValue(value),
            onSheetRender: (value) => formatSightValue(value),
        },
        {
            display: '戴鏡左', property: 'sightL', style: { width: "4%" },
            onRender: (value) => formatSightValue(value),
            onSheetRender: (value) => formatSightValue(value),
        },
        {
            display: '散瞳',
            property: 'isDilating',
            onRender: boolRender,
            onSheetRender: boolRender,
            style: { width: "3%" }
        },
        { display: '近視右', property: 'eNearR', style: { width: "4%" } },
        { display: '近視左', property: 'eNearL', style: { width: "4%" } },
        { display: '遠視右', property: 'eFarR', style: { width: "4%" } },
        { display: '遠視左', property: 'eFarL', style: { width: "4%" } },
        { display: '散光右', property: 'eSanR', style: { width: "4%" } },
        { display: '散光左', property: 'eSanL', style: { width: "4%" } },
        { display: '屈光右', property: 'diopR', style: { width: "4%" } },
        { display: '屈光左', property: 'diopL', style: { width: "4%" } },
        { display: '診斷', property: 'diag' },
        { display: '備註', property: 'eSight99State' },
        {
            display: '處置代號',
            property: 'sightManageCodes',
            onSheetRender: (v) => (Array.isArray(v) ? v.join(',') : (v as string)),
            onRender: (v) => (Array.isArray(v) ? v.join(',') : (v as string)),
        },
        {
            display: '醫師建議處置',
            property: 'sightManageCodes',
            onRender: (v) => {
                if (Array.isArray(v)) {
                    return v.map(id => I18N.SightManageCodeMap[id] || '').join(', ');
                }
                return '';
            },
            onSheetRender: (v) => {
                if (Array.isArray(v)) {
                    return v.map(id => I18N.SightManageCodeMap[id] || '').join(', ');
                }
                return '';
            },
        },
        {
            display: '定期檢查', property: 'periodical',
            onRender: (v) => {
                return v instanceof DateTime ? v.toFormat('yyyy/MM/dd') : null;
            },
            onSheetRender: (v) => {
                return v instanceof DateTime ? v.toFormat('yyyy/MM/dd') : "";
            },
        },
    ];

const mapState = (app: ApplicationState) => ({ ...app.auth });
const mapDispatch = ErrorDispatches;

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;

type Argument = {
    yearSem?: YearSem;
};

const useHighNearSight = ({
    yearSem
}: Argument): {
    highnearSightId?: string;
    element: ReactNode;
} => {
    const [chooseId, settype] = useState<string>();
    return {
        highnearSightId: chooseId,
        element: (
            <>
                <DropdownButton
                    title={
                        yearSem
                            ? (chooseId) || '請選擇超過度數'
                            : '請先選擇學年'
                    }
                    onSelect={(k: string | null) => {
                        if (k == null) return;
                        settype(k);
                    }}
                >
                    <Dropdown.Item eventKey="100">100</Dropdown.Item>
                    <Dropdown.Item eventKey="200">200</Dropdown.Item>
                    <Dropdown.Item eventKey="300">300</Dropdown.Item>
                    <Dropdown.Item eventKey="400">400</Dropdown.Item>
                    <Dropdown.Item eventKey="500">500</Dropdown.Item>
                    <Dropdown.Item eventKey="600">600</Dropdown.Item>
                    <Dropdown.Item eventKey="700">700</Dropdown.Item>
                    <Dropdown.Item eventKey="800">800</Dropdown.Item>
                    <Dropdown.Item eventKey="900">900</Dropdown.Item>
                    <Dropdown.Item eventKey="1000">1000</Dropdown.Item>
                </DropdownButton>
            </>
        ),
    };
};

const sightHightNear: FunctionComponent<Props> = ({
    user,
    catchErrorForModal,
}) => {
    const { yearSem, element: semGradeElement } = useSemGrade();
    const { highnearSightId, element: highNearElement } =
        useHighNearSight({
            yearSem,
        });
    const [students, setStudents] = useState<PageData[]>([]);
    const [userinfo, setUser] = useState<UserInfo>();
    useEffect(() => {
        if (yearSem && highnearSightId) {
            apis.getUserInfo()
                .then((u) => {
                    setUser(u);
                })
                .catch(catchErrorForModal);
            toast
                .promise(
                    apis
                        .getSightHighENear(yearSem.year, yearSem.sem, Number(highnearSightId))
                        .then(r => {
                            setStudents(
                                r.map(({ sight, ...s }) => ({
                                    ...sight,
                                    ...s,
                                    diag: sightDiag(sight),
                                }))
                            )
                        }),
                    {
                        pending: '資料載入中......',
                        success: '資料載入成功！',
                        error: '資料載入失敗！請查看錯誤資訊。',
                    }
                )
                .catch(catchErrorForModal);


        }
    }, [highnearSightId, yearSem?.year, yearSem?.sem]);

    return (
        <AuthedLayout>
            <Row className="justify-content-between">
                <Col>
                    <Row>
                        <Col xs={3} className="mr-3">
                            {semGradeElement}
                        </Col>
                        <Col xs={3} className="mr-3">
                            <Row>
                                {highNearElement}
                            </Row>
                        </Col>
                    </Row>
                </Col>
                <div>
                    <DropdownButton
                        id="sight-high-near-export"
                        title="匯出"
                        variant="success"
                        disabled={!students.length}
                    >
                        <Dropdown.Item
                            onClick={() => {
                                downloadDataAsExcel({
                                    title:
                                        `${user.schools[user.currentSchool].name}` +
                                        `${yearSem?.year}學年` +
                                        `${yearSem?.sem}學期_近視度數超過` +
                                        `${highnearSightId}度名單`,
                                    values: students,
                                    headers: tableHeader,
                                    context: SightContext,
                                    groupBy: () => `${highnearSightId}度`,
                                    footer:
                                        '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
                                    exportfile: userinfo?.exportFileType,
                                });
                            }}
                        >
                            Excel 下載
                        </Dropdown.Item>

                        <Dropdown.Item
                            onClick={() => {
                                printDataAsTable({
                                    title:
                                        `${user.schools[user.currentSchool].name}` +
                                        `${yearSem?.year}學年` +
                                        `${yearSem?.sem}學期_近視度數超過` +
                                        `${highnearSightId}度名單`,
                                    values: students,
                                    headers: tableHeader,
                                    context: SightContext,
                                    groupBy: () => `${highnearSightId}度`,
                                    footer:
                                        '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
                                });
                            }}
                        >
                            列印
                        </Dropdown.Item>
                    </DropdownButton>
                </div>
            </Row>
            <hr />
            <Row>
                <EditableTableDiv
                    headers={tableHeader}
                    values={students}
                    context={SightContext}
                />
            </Row>
        </AuthedLayout>
    );
};

function boolRender(v: unknown) {
    return v ? '是' : '否';
}
export const SightHightNear = connector(sightHightNear);
